import { CSSObject, DefaultMantineColor, MantineThemeOther, MantineThemeOverride, Tuple } from "@mantine/core";

type ExtendedCustomColors =
  | DefaultMantineColor
  | "text"
  | "sky"
  | "brand"
  | "spark"
  | "mellow"
  | "calm"
  | "amethyst"
  | "green"
  | "darkMode";

import { rpaLightTheme } from "./themeColors";

const semanticColors = {
  // "old" theme
  textInverse: "#ffffff",
  textIconInverse: "#F5F5F5",
  primaryButton: "#6A49B6",
  primaryButtonHover: "#b1aec4",
  primaryButtonActive: "#504a73",
  borderFocus: "#46bbea",
  borderSubtle: "#989898",
  inputBackground: "#ffffff",
  textIconBold: "#3D3D3D",
  textBold: "#292929",
  textSubtle: "#464646",
  darkContentPaneBackground: "#320161",
  alertDangerBorder: "#BD1818",
  alertDangerBackground: "#FEF2F2",
  divider: "#888888",
  headerDivider: "#CBCBCB",
  successBackground: "#166640",
  link: "#6A49B6",

  // "new" theme
  ...rpaLightTheme,
};

const sizes = {
  "body-text-lineheight": "140%",
  "alert-content-lineheight": "140%",
  "icon-xs": 16,
  "icon-md": 24,
  md: 40,
};

declare module "@mantine/core" {
  // @ts-expect-error type redeclaration was they way Mantine wanted you to do this, leaving it
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, Tuple<string, 10>>;
  }

  // @ts-expect-error type redeclaration was they way Mantine wanted you to do this, leaving it
  export interface MantineThemeOther {
    fontWeights: Partial<Record<"light" | "regular" | "roman" | "medium" | "bold" | "semibold" | "normal", number>>;
    fontTypes: Record<
      "d1" | "d2" | "h1" | "h2" | "h3" | "h4" | "p1" | "p2" | "p3" | "button" | "link" | "label" | "error",
      CSSObject
    >;
    boxShadows: Record<"button" | "buttonHover" | "large" | "darkMode", string>;
    scrollbars: Record<"light", CSSObject>;
    sizes: typeof sizes;
    semanticColors: typeof semanticColors;
  }
}

const fontWeights: MantineThemeOther["fontWeights"] = {
  light: 300,
  regular: 400,
  normal: 400,
  roman: 450,
  medium: 500,
  semibold: 600,
  bold: 700,
};

const fontTypes: MantineThemeOther["fontTypes"] = {
  d1: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 64,
    lineHeight: "64px",
  },
  d2: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 40,
    lineHeight: "48px",
  },
  h1: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 34,
    lineHeight: "36px",
  },
  h2: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 24,
  },
  h3: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 20,
    lineHeight: "24px",
  },
  h4: {
    fontFamily: "neue-haas-grotesk-display",
    fontSize: 16,
    letterSpacing: "1px",
  },
  p1: {
    fontFamily: "sofia-pro",
    fontSize: 20,
    lineHeight: "24px",
  },
  p2: {
    fontFamily: "sofia-pro",
    fontSize: 16,
  },
  p3: {
    fontFamily: "sofia-pro",
    fontSize: 14,
    lineHeight: "19.6px",
  },
  button: {
    fontFamily: "sofia-pro",
    fontSize: 16,
  },
  link: {
    fontFamily: "sofia-pro",
    fontSize: 16,
    lineHeight: "16px",
  },
  label: {
    fontFamily: "sofia-pro",
    fontSize: 14,
  },
  error: {
    fontFamily: "sofia-pro",
    fontSize: 14,
  },
};

const boxShadows: MantineThemeOther["boxShadows"] = {
  button: "0px 4px 4px 0px #00000026",
  buttonHover: "0px 4px 4px 0px #0000000D",
  large: "0px 10px 40px 0px #0000001A",
  darkMode: "0px 16px 32px rgba(0, 0, 0, 0.37), 0px 2px 10.67px rgba(0, 0, 0, 0.37)",
};

const scrollbars: MantineThemeOther["scrollbars"] = {
  light: {
    "::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.8)",
      borderRadius: 100,
    },
  },
};

export const themeOther: MantineThemeOther = {
  fontWeights,
  fontTypes,
  boxShadows,
  scrollbars,
  sizes,
  semanticColors,
};

export const rpiTheme: MantineThemeOverride = {
  fontFamily: "'sofia-pro', sans-serif",
  headings: {
    fontWeight: 500,
    fontFamily: "'sofia-pro', sans-serif",
  },
  primaryShade: 5,
  primaryColor: "brand",
  black: "#050203",
  other: themeOther,
  colors: {
    // [/, /, LIGHTEST, LIGHTER, LIGHT, BASE, DARK, DARKER, DARKEST, /]
    text: [
      "#b3b3b3",
      "#b3b3b3",
      "#b3b3b3",
      "#999999",
      "#5c5c5c",
      "#050203",
      "#050203",
      "#050203",
      "#050203",
      "#050203",
    ],
    sky: ["#ffffff", "#ffffff", "#ffffff", "#ffffff", "#ffffff", "#ffffff", "#f2f2f2", "#e6e6e6", "#d9d9d9", "#d9d9d9"],
    brand: [
      "#b1aec4",
      "#b1aec4",
      "#b1aec4",
      "#b1aec4",
      "#827cac",
      "#655c94",
      "#5a5382",
      "#504a73",
      "#504a73",
      "#504a73",
    ],
    spark: [
      "#fadeda",
      "#fadeda",
      "#fadeda",
      "#f5beb5",
      "#f5beb5",
      "#f56b55",
      "#b93a2e",
      "#b93a2e",
      "#b93a2e",
      "#b93a2e",
    ],
    mellow: [
      "#e7d54f",
      "#e7d54f",
      "#e7d54f",
      "#e7d54f",
      "#e7d54f",
      "#e7d54f",
      "#ddd179",
      "#ddd179",
      "#ddd179",
      "#ddd179",
    ],
    calm: [
      "#f4f7fa",
      "#f4f7fa",
      "#f4f7fa",
      "#f4f7fa",
      "#f4f7fa",
      "#bcccdd",
      "#6da2ec",
      "#6da2ec",
      "#6da2ec",
      "#6da2ec",
    ],
    amethyst: [
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
      "#a57fa0",
    ],
    green: [
      "#9cf0d3",
      "#9cf0d3",
      "#9cf0d3",
      "#9cf0d3",
      "#9cf0d3",
      "#52c8b1",
      "#52c8b1",
      "#52c8b1",
      "#52c8b1",
      "#52c8b1",
    ],
    darkMode: [
      "#2f2e2e",
      "#2f2e2e",
      "#2f2e2e",
      "#2f2e2e",
      "#272626",
      "#242323",
      "#242323",
      "#242323",
      "#242323",
      "#242323",
    ],
  },
};
